import { FC } from 'react';
import classNames from 'classnames';
import { usePathname, useSearchParams } from 'next/navigation';

import { usePagination } from 'opensearch';
import { IconSvg, Link } from '@/components';

import styles from './CollectionTreezPagination.module.scss';

interface PaginationProps {
  className?: string;
  onPageChange?: () => void;
}

const CollectionTreezPagination: FC<PaginationProps> = ({
  className,
  onPageChange,
}) => {
  const { pages, currentPage, totalPages, isFirstPage, isLastPage, canRefine } =
    usePagination({ padding: 2 });

  if (!canRefine) return null;

  return (
    <nav
      aria-label="Pagination"
      className={classNames(styles.pagination_container, className)}
    >
      <ol className={styles.pagination} role="list">
        <li>
          <PaginationItem
            ariaLabel="Go to first page"
            className={classNames(
              styles.pagination_item_link_action,
              styles.pagination_item_link_icon_left_position,
              {
                [styles.disabled]: isFirstPage,
              },
            )}
            disabled={isFirstPage}
            icon="chevron-double-left"
            label="First"
            onClick={onPageChange}
            page={0}
          />
        </li>
        <li>
          <PaginationItem
            ariaLabel="Go to previous page"
            className={classNames(
              styles.pagination_item_link_action,
              styles.pagination_item_link_icon_left_position,
              {
                [styles.disabled]: isFirstPage,
              },
            )}
            disabled={isFirstPage}
            icon="chevron-left"
            label="Previous"
            onClick={onPageChange}
            page={currentPage - 1}
          />
        </li>
        {pages.map(page => (
          <li key={page}>
            <PaginationItem
              ariaLabel={`Go to page ${page + 1}`}
              className={classNames(styles.pagination_item_link, {
                [styles.active]: currentPage === page,
              })}
              isCurrent={currentPage === page}
              onClick={onPageChange}
              page={page}
            />
          </li>
        ))}
        <li>
          <PaginationItem
            ariaLabel="Go to next page"
            className={classNames(styles.pagination_item_link_action, {
              [styles.disabled]: isLastPage,
            })}
            disabled={isLastPage}
            icon="chevron-right-pagination"
            label="Next"
            onClick={onPageChange}
            page={currentPage + 1}
          />
        </li>
        <li>
          <PaginationItem
            ariaLabel="Go to last page"
            className={classNames(styles.pagination_item_link_action, {
              [styles.disabled]: isLastPage,
            })}
            disabled={isLastPage}
            icon="chevron-double-right"
            label="Last"
            onClick={onPageChange}
            page={totalPages - 1}
          />
        </li>
      </ol>
    </nav>
  );
};

type IconName =
  | 'chevron-double-left'
  | 'chevron-double-right'
  | 'chevron-left'
  | 'chevron-right-pagination'
  | null;

const PaginationItem: FC<{
  page: number;
  label?: string;
  icon?: IconName;
  className?: string;
  isCurrent?: boolean;
  ariaLabel?: string;
  disabled?: boolean;
  onClick?: () => void;
}> = ({
  page,
  label,
  icon,
  className,
  isCurrent,
  ariaLabel,
  disabled = false,
  onClick,
}) => {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const createPaginationUrl = (pageNumber: number) => {
    const params = new URLSearchParams(searchParams.toString());
    params.set('page', (pageNumber + 1).toString());
    return `${pathname}?${params.toString()}`;
  };

  const content = (
    <>
      <span>{label ?? page + 1}</span>
      {icon && <IconSvg decorative={false} name={icon} />}
    </>
  );

  if (disabled) {
    return (
      <span
        aria-disabled="true"
        aria-label={ariaLabel}
        className={classNames(styles.pagination_item_link, className)}
      >
        {content}
      </span>
    );
  }

  return (
    <Link
      aria-current={isCurrent ? 'page' : undefined}
      aria-label={ariaLabel}
      className={classNames(styles.pagination_item_link, className)}
      href={createPaginationUrl(page)}
      onClick={onClick}
      scroll={false}
    >
      {content}
    </Link>
  );
};

export { CollectionTreezPagination };
